/**
 * SquareTrainerAdapter
 *
 * Battle adapter for `BattleType.SQUARE_TRAINER`. Renders the board
 * (`FunChessboard`) with the current coord prompt overlaid as a teal tile in
 * the centre of the board (lifted from NSCL's BattleScreen for visual
 * parity). Emits `onMove(correct, square)` on every click.
 *
 * The server-pushed coord array is the source of truth — both players answer
 * the same squares in the same order.
 *
 * Owns:
 *   - transient highlight (350 ms) for click feedback
 *   - the central coord prompt overlay
 *
 * Does NOT own:
 *   - the timer (the BattleScreen drives it from the server `endsAt`)
 *   - the index (BattleScreen passes `myIndex`; the store advances it)
 *   - move recording (the façade `useFunChallengesBattleSocket.sendMove` does that)
 *   - sound (handled by `useBattleSoundEffects` in BattleScreen so the same
 *     hook can also play the timer-warning + start countdown sounds)
 *
 * Self-registers with the FunChallenges registry on import.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {StyleSheet, Text, View} from 'react-native';

import CCColors from '@/Utils/CCColors';
import useBattleSoundEffects from '@/Hooks/FunChallengesSounds/useBattleSoundEffects';

import {BattleType} from '../types';
import {registerAdapter, type BattleAdapter} from '../registry';
import FunChessboard from '../components/FunChessboard';

/** Props the BattleScreen passes to `adapter.renderGame(props)`. */
export interface SquareTrainerAdapterProps {
  /** Server-pre-rolled coord sequence (length 300). Identical for both players. */
  coords: string[];
  /** Local player's current index into `coords`. */
  myIndex: number;
  /** Board orientation for this player. */
  orientation: 'white' | 'black' | 'random';
  /** Pixel side-length of the board. */
  boardWidth: number;
  /** Click → `onMove(correct, square)`. The façade enqueues the server message. */
  onMove: (correct: boolean, square: string) => void;
  /** When true, the board ignores clicks (game ended / not yet started). */
  disabled?: boolean;
}

const HIGHLIGHT_MS = 350;

// Matches NSCL's overlay (rgba(0, 200, 100, 0.7) / rgba(220, 50, 50, 0.7)).
const CORRECT_HIGHLIGHT = 'rgba(0, 200, 100, 0.7)';
const WRONG_HIGHLIGHT = 'rgba(220, 50, 50, 0.7)';

function resolveOrientation(o: SquareTrainerAdapterProps['orientation']): 'white' | 'black' {
  if (o === 'black') return 'black';
  if (o === 'random') {
    return Math.random() < 0.5 ? 'white' : 'black';
  }
  return 'white';
}

export function SquareTrainerAdapterView(props: SquareTrainerAdapterProps) {
  const {coords, myIndex, orientation, boardWidth, onMove, disabled = false} =
    props;

  // Wrap on read so a very fast player can keep clicking past coords.length
  // (server pre-rolls 300; capped headroom can still be exhausted at
  // sustained > 3.3 clicks/s for the full 90 s timer). Server doesn't care
  // about coord position — it just records the clicked square and counts
  // correct/wrong, so wrapping is a pure client read.
  const targetSquare =
    coords.length > 0 ? coords[myIndex % coords.length] ?? '' : '';

  const [highlight, setHighlight] = useState<{
    square: string;
    color: string;
  } | null>(null);

  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {playCorrect, playWrong} = useBattleSoundEffects();

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // If the game disables (e.g. timer expires), clear any in-flight highlight.
  useEffect(() => {
    if (disabled) {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      setHighlight(null);
    }
  }, [disabled]);

  const resolvedOrientation = useMemo(
    () => resolveOrientation(orientation),
    [orientation],
  );

  const handleSquareClick = (square: string) => {
    if (disabled) return;
    if (!targetSquare) return;

    const correct = square === targetSquare;

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlight({
      square,
      color: correct ? CORRECT_HIGHLIGHT : WRONG_HIGHLIGHT,
    });
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlight(null);
    }, HIGHLIGHT_MS);

    if (correct) playCorrect();
    else playWrong();

    onMove(correct, square);
  };

  // Scale the central prompt tile with the board so it stays readable on
  // small viewports too. Sized smaller than the NSCL reference (96 px) per
  // user feedback — the dominant element should be the board itself.
  const promptSize = Math.max(56, Math.min(84, boardWidth * 0.16));
  const promptFontSize = promptSize * 0.48;

  return (
    <View
      style={{
        position: 'relative',
        width: boardWidth,
        height: boardWidth,
        alignSelf: 'center',
      }}>
      <FunChessboard
        boardWidth={boardWidth}
        orientation={resolvedOrientation}
        highlightedSquare={highlight?.square ?? null}
        highlightColor={highlight?.color ?? 'transparent'}
        onSquareClick={handleSquareClick}
        disabled={disabled}
      />
      {!disabled && !!targetSquare && (
        <View pointerEvents="none" style={StyleSheet.absoluteFill}>
          <View style={styles.coordOverlay}>
            <View
              style={[
                styles.coordTile,
                {
                  width: promptSize,
                  height: promptSize,
                  borderRadius: 12,
                },
              ]}>
              <Text style={[styles.coordText, {fontSize: promptFontSize}]}>
                {targetSquare}
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  coordOverlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  coordTile: {
    backgroundColor: 'rgba(61, 171, 158, 0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    elevation: 6,
  },
  coordText: {
    fontWeight: '800',
    color: CCColors.White,
    letterSpacing: 0.5,
  },
});

const adapter: BattleAdapter = {
  battleType: BattleType.SQUARE_TRAINER,
  renderGame: (props: SquareTrainerAdapterProps) => (
    <SquareTrainerAdapterView {...props} />
  ),
};

registerAdapter(adapter);

export default adapter;
